import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { retrieveProfileData } from "../../api/PupilApi";
import { toast } from "react-toastify";
import { chitOnboardByDateRange } from "../../api/PupilApi";

const MoneyChitDashboard = () => {
  const [chits, setChits] = useState([]);
  const [selectedChit, setSelectedChit] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [memberNames, setMemberNames] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchChitData = async () => {
      try {
        const currentYear = new Date().getFullYear();
        const startDate = new Date(`${currentYear}-01-01`);
        const endDate = new Date(`${currentYear}-12-31`);
        const formatDate = (date) => date.toISOString().split("T")[0];

        const res = await chitOnboardByDateRange(
          formatDate(startDate),
          formatDate(endDate),
          "chit"
        );

        const moneyChits =
          (res?.data || [])
            .filter((item) => item.chitData)
            .map((item, index) => ({
              ...item.chitData,
              pupilEmail: item.pupilEmail || null,
              chitId: item.chitData.planId || index + 1,
              category: item.category || "chit",
              createdAt: item.createdAt || null,
              // Ensure we have the correct planId
              planId: item.chitData.planId,
            })) || [];

        setChits(moneyChits);
        toast.success("Fetched Money Chit Data Successfully!");
      } catch (err) {
        toast.error("Error fetching money chit data: " + (err?.message || err));
      }
    };

    fetchChitData();
  }, []);

  // Function to load member names for a specific chit
  const loadMemberNames = async (chitMemberList) => {
    if (!chitMemberList || chitMemberList.length === 0) return;

    const namesMap = {};
    const namePromises = chitMemberList.map(async (member) => {
      if (member.pupilEmail && !memberNames[member.pupilEmail]) {
        try {
          const profile = await retrieveProfileData(member.pupilEmail);
          if (profile.data && profile.data.name) {
            namesMap[member.pupilEmail] = profile.data.name;
          }
        } catch (err) {
          console.error("Error loading profile for:", member.pupilEmail, err);
          namesMap[member.pupilEmail] = member.pupilEmail;
        }
      } else if (member.pupilEmail && memberNames[member.pupilEmail]) {
        namesMap[member.pupilEmail] = memberNames[member.pupilEmail];
      }
    });

    await Promise.all(namePromises);
    setMemberNames((prev) => ({ ...prev, ...namesMap }));
  };

  const handleViewProfile = async (email, chitId) => {
    if (!email) {
      toast.error("No email associated with this member");
      return;
    }

    setLoadingProfile(true);
    try {
      const profile = await retrieveProfileData(email);
      
      // Filter profile data to show only relevant chit information
      const filteredProfileData = {
        ...profile.data,
        currentChitId: chitId,
        currentChitTitle: selectedChit?.chitTitle || "Unknown Chit",
        chitSpecificData: profile.data?.chits?.find(chit => chit.chitId === chitId) || null
      };
      
      setProfileData(filteredProfileData);
    } catch (err) {
      setProfileData({ error: "Failed to load profile data" });
      toast.error("Error loading profile: " + (err?.message || err));
    } finally {
      setLoadingProfile(false);
    }
  };

  // Helper to format date
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "-";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Format currency values
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "-";
    return `₹${amount.toLocaleString()}`;
  };

  // Get member name - use stored name or fallback to email
  const getMemberName = (email) => {
    return memberNames[email] || email || "-";
  };

  // Navigate to profile view page with proper parameters including planId
  const navigateToProfileView = (member, chit) => {
    if (!member.pupilEmail) {
      toast.error("No email associated with this member");
      return;
    }

    navigate(
      `/profileviewMoneyChit/${member.pupilEmail}?planId=${chit.planId}&chitId=${chit.chitId}&chitTitle=${encodeURIComponent(chit.chitTitle)}&chitValue=${chit.chitValue}`
    );
  };

  // Group chits by type (1 lakh or 5 lakhs)
  const groupChitsByType = (chits) => {
    const grouped = {
      oneLakh: [],
      fiveLakhs: []
    };

    chits.forEach(chit => {
      if (chit.chitValue === 100000) {
        grouped.oneLakh.push(chit);
      } else if (chit.chitValue === 500000) {
        grouped.fiveLakhs.push(chit);
      }
    });

    return grouped;
  };

  const groupedChits = groupChitsByType(chits);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-center">
        Money Chit Dashboard
      </h1>

      {/* Chit List Grid */}
      {!selectedChit ? (
        <>
          {chits.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No Money Chit data available.
            </div>
          ) : (
            <div className="space-y-8">
              {/* 1 Lakh Chits Section */}
              {groupedChits.oneLakh.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold mb-4 text-blue-600 border-b-2 border-blue-200 pb-2">
                    1 Lakh Chits ({groupedChits.oneLakh.length})
                  </h2>
                  <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {groupedChits.oneLakh.map((chit) => (
                      <div
                        key={chit.planId} // Use planId as key for uniqueness
                        className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-blue-200 flex flex-col cursor-pointer"
                        onClick={() => {
                          setSelectedChit(chit);
                          setSelectedMember(null);
                          setProfileData(null);
                        }}
                        tabIndex={0}
                        onKeyPress={(e) => e.key === "Enter" && setSelectedChit(chit)}
                      >
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 text-white text-center">
                          <h3 className="text-lg font-semibold truncate">
                            {chit.chitTitle}
                          </h3>
                          <p className="text-sm text-blue-100 mt-1">
                            Value: {formatCurrency(chit.chitValue)}
                          </p>
                          <p className="text-xs text-blue-200 mt-1">
                            Plan ID: {chit.planId}
                          </p>
                        </div>
                        <div className="p-3 flex-1 flex flex-col">
                          <div className="flex justify-between text-xs text-gray-600 mb-2">
                            <div className="bg-gray-50 p-2 rounded-lg text-center flex-1 mr-1">
                              <p className="font-medium">Start</p>
                              <p className="font-semibold text-gray-800">
                                {formatDisplayDate(chit.startDate)}
                              </p>
                            </div>
                            <div className="bg-gray-50 p-2 rounded-lg text-center flex-1 ml-1">
                              <p className="font-medium">End</p>
                              <p className="font-semibold text-gray-800">
                                {formatDisplayDate(chit.endDate)}
                              </p>
                            </div>
                          </div>
                          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-2 mb-2 text-center border border-teal-100">
                            <p className="text-xs text-gray-600">Monthly Amount</p>
                            <p className="text-lg font-bold text-teal-700">
                              {formatCurrency(chit.fullChitBidderAmount)}
                            </p>
                          </div>
                          <div className="text-xs text-gray-600 mb-2 space-y-1">
                            <p>
                              <span className="font-semibold">Members:</span>{" "}
                              {chit.chitMemberList?.length || 0}
                            </p>
                            <p>
                              <span className="font-semibold">Months:</span>{" "}
                              {chit.months}
                            </p>
                            <p>
                              <span className="font-semibold">Monthly Decrease:</span>{" "}
                              {formatCurrency(chit.monthlyDecrease)}
                            </p>
                          </div>
                          <div className="mt-auto">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedChit(chit);
                                setSelectedMember(null);
                                setProfileData(null);
                              }}
                              className="w-full py-2 px-4 rounded-lg font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 5 Lakhs Chits Section */}
              {groupedChits.fiveLakhs.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold mb-4 text-green-600 border-b-2 border-green-200 pb-2">
                    5 Lakhs Chits ({groupedChits.fiveLakhs.length})
                  </h2>
                  <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {groupedChits.fiveLakhs.map((chit) => (
                      <div
                        key={chit.planId} // Use planId as key for uniqueness
                        className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-green-200 flex flex-col cursor-pointer"
                        onClick={() => {
                          setSelectedChit(chit);
                          setSelectedMember(null);
                          setProfileData(null);
                        }}
                        tabIndex={0}
                        onKeyPress={(e) => e.key === "Enter" && setSelectedChit(chit)}
                      >
                        <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 text-white text-center">
                          <h3 className="text-lg font-semibold truncate">
                            {chit.chitTitle}
                          </h3>
                          <p className="text-sm text-green-100 mt-1">
                            Value: {formatCurrency(chit.chitValue)}
                          </p>
                          <p className="text-xs text-green-200 mt-1">
                            Plan ID: {chit.planId}
                          </p>
                        </div>
                        <div className="p-3 flex-1 flex flex-col">
                          <div className="flex justify-between text-xs text-gray-600 mb-2">
                            <div className="bg-gray-50 p-2 rounded-lg text-center flex-1 mr-1">
                              <p className="font-medium">Start</p>
                              <p className="font-semibold text-gray-800">
                                {formatDisplayDate(chit.startDate)}
                              </p>
                            </div>
                            <div className="bg-gray-50 p-2 rounded-lg text-center flex-1 ml-1">
                              <p className="font-medium">End</p>
                              <p className="font-semibold text-gray-800">
                                {formatDisplayDate(chit.endDate)}
                              </p>
                            </div>
                          </div>
                          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-2 mb-2 text-center border border-teal-100">
                            <p className="text-xs text-gray-600">Monthly Amount</p>
                            <p className="text-lg font-bold text-teal-700">
                              {formatCurrency(chit.fullChitBidderAmount)}
                            </p>
                          </div>
                          <div className="text-xs text-gray-600 mb-2 space-y-1">
                            <p>
                              <span className="font-semibold">Members:</span>{" "}
                              {chit.chitMemberList?.length || 0}
                            </p>
                            <p>
                              <span className="font-semibold">Months:</span>{" "}
                              {chit.months}
                            </p>
                            <p>
                              <span className="font-semibold">Monthly Decrease:</span>{" "}
                              {formatCurrency(chit.monthlyDecrease)}
                            </p>
                          </div>
                          <div className="mt-auto">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedChit(chit);
                                setSelectedMember(null);
                                setProfileData(null);
                              }}
                              className="w-full py-2 px-4 rounded-lg font-medium text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-xl shadow-lg md:shadow-xl max-w-5xl mx-auto w-full p-4 md:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold">
                {selectedChit.chitTitle}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Plan ID: {selectedChit.planId} • Chit ID: {selectedChit.chitId} • Value: {formatCurrency(selectedChit.chitValue)}
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedChit(null);
                setSelectedMember(null);
                setProfileData(null);
              }}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-400 transition-colors"
            >
              Back to Chit List
            </button>
          </div>

          {/* Member List Table */}
          <div className="mb-6">
            <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-3">
              Members List (Plan ID: {selectedChit.planId})
            </h3>
            <div className="overflow-x-auto rounded-lg shadow border border-gray-200">
              <table className="min-w-full bg-white text-xs md:text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-3 px-3 border-b text-left font-semibold">Name</th>
                    <th className="py-3 px-3 border-b text-left font-semibold">Mobile</th>
                    <th className="py-3 px-3 border-b text-left font-semibold">Aadhar</th>
                    <th className="py-3 px-3 border-b text-left font-semibold">PAN</th>
                    <th className="py-3 px-3 border-b text-left font-semibold">Member Plan ID</th>
                    <th className="py-3 px-3 border-b text-center font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedChit.chitMemberList?.length > 0 ? (
                    selectedChit.chitMemberList.map((member, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 border-b">
                        <td className="py-3 px-3">
                          {member.fullName || "-"}
                        </td>
                        <td className="py-3 px-3">
                          {member.mobileNumber || "-"}
                        </td>
                        <td className="py-3 px-3">
                          {member.aadhar || "-"}
                        </td>
                        <td className="py-3 px-3">
                          {member.pan || "-"}
                        </td>
                        <td className="py-3 px-3">
                          {member.planId || "-"}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <button
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs transition-colors"
                            onClick={() => navigateToProfileView(member, selectedChit)}
                            aria-label={`View profile of ${member.fullName || member.pupilEmail}`}
                          >
                            View Profile
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="py-4 text-center text-gray-500">
                        No members found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chit Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-700 mb-2 text-sm">Start Date</h3>
              <p className="text-lg font-medium">{formatDisplayDate(selectedChit.startDate)}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-700 mb-2 text-sm">End Date</h3>
              <p className="text-lg font-medium">{formatDisplayDate(selectedChit.endDate)}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-700 mb-2 text-sm">Chit Value</h3>
              <p className="text-lg font-medium">{formatCurrency(selectedChit.chitValue)}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-700 mb-2 text-sm">Months</h3>
              <p className="text-lg font-medium">{selectedChit.months}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-700 mb-2 text-sm">Monthly Decrease</h3>
              <p className="text-lg font-medium">{formatCurrency(selectedChit.monthlyDecrease)}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-700 mb-2 text-sm">Members</h3>
              <p className="text-lg font-medium">{selectedChit.chitMemberList?.length || 0}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 md:col-span-2">
              <h3 className="font-semibold text-gray-700 mb-2 text-sm">Full Chit Bidder Amount</h3>
              <p className="text-lg font-medium">{formatCurrency(selectedChit.fullChitBidderAmount)}</p>
            </div>
          </div>

          {/* Schedule Data Table */}
          <div className="mb-6">
            <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-3">
              Chit Schedule - {selectedChit.chitTitle} (Plan ID: {selectedChit.planId})
            </h3>
            <div className="overflow-x-auto rounded-lg shadow border border-gray-200">
              <table className="min-w-full bg-white text-xs md:text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-3 px-3 border-b text-center font-semibold">Month</th>
                    <th className="py-3 px-3 border-b text-center font-semibold">Full Non-Settlement Chit</th>
                    <th className="py-3 px-3 border-b text-center font-semibold">Half Non-Settlement Chit</th>
                    <th className="py-3 px-3 border-b text-center font-semibold">Full Settlement Chit</th>
                    <th className="py-3 px-3 border-b text-center font-semibold">Half Settlement Chit</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedChit.scheduleData?.filter((item) => item.month !== 0).length > 0 ? (
                    selectedChit.scheduleData
                      .filter((item) => item.month !== 0)
                      .map((data, index) => (
                        <tr key={index} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                          <td className="py-2 px-3 border-b text-center font-medium">{data.month}</td>
                          <td className="py-2 px-3 border-b text-center">{formatCurrency(data.fullChit)}</td>
                          <td className="py-2 px-3 border-b text-center">{formatCurrency(data.halfNonChit)}</td>
                          <td className="py-2 px-3 border-b text-center">{formatCurrency(data.fullNonchit)}</td>
                          <td className="py-2 px-3 border-b text-center">{formatCurrency(data.halfChit)}</td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="py-4 text-center text-gray-500">
                        No schedule data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Inline Profile View Modal */}
          {selectedMember && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-6 border-b">
                  <h2 className="text-xl font-bold">Member Details</h2>
                  <button
                    onClick={() => {
                      setSelectedMember(null);
                      setProfileData(null);
                    }}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                    aria-label="Close member details"
                  >
                    &times;
                  </button>
                </div>

                <div className="p-6">
                  {/* Current Chit Info */}
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-blue-800 text-sm">Current Chit</h3>
                    <p className="text-blue-700">{selectedChit.chitTitle}</p>
                    <p className="text-blue-600 text-xs">Plan ID: {selectedChit.planId} • Chit ID: {selectedChit.chitId}</p>
                  </div>

                  {/* Member Basic Info */}
                  <table className="min-w-full bg-white border border-gray-200 mb-4 text-sm">
                    <tbody>
                      <tr>
                        <td className="py-2 px-4 font-semibold bg-gray-50">Name</td>
                        <td className="py-2 px-4">{getMemberName(selectedMember.pupilEmail)}</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-4 font-semibold bg-gray-50">Email</td>
                        <td className="py-2 px-4">{selectedMember.pupilEmail || "-"}</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-4 font-semibold bg-gray-50">Mobile</td>
                        <td className="py-2 px-4">{selectedMember.mobileNumber || "-"}</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-4 font-semibold bg-gray-50">Aadhar</td>
                        <td className="py-2 px-4">{selectedMember.aadhar || "-"}</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-4 font-semibold bg-gray-50">PAN</td>
                        <td className="py-2 px-4">{selectedMember.pan || "-"}</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-4 font-semibold bg-gray-50">Member Plan ID</td>
                        <td className="py-2 px-4">{selectedMember.planId || "-"}</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Profile Data Section */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold">Profile Data</h3>
                      <button
                        onClick={() => handleViewProfile(selectedMember.pupilEmail, selectedChit.planId)}
                        className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm transition-colors"
                        disabled={loadingProfile}
                      >
                        {loadingProfile ? "Loading..." : "Load Profile"}
                      </button>
                    </div>

                    {loadingProfile ? (
                      <div className="text-center text-gray-500 py-4">
                        Loading profile data...
                      </div>
                    ) : profileData ? (
                      profileData.error ? (
                        <div className="text-center text-red-500 py-4 bg-red-50 rounded-lg">
                          {profileData.error}
                        </div>
                      ) : (
                        <div>
                          <div className="mb-3 p-2 bg-green-50 rounded border border-green-200">
                            <p className="text-sm text-green-700">
                              <strong>Filtered for:</strong> {selectedChit.chitTitle} (Plan ID: {selectedChit.planId})
                            </p>
                          </div>
                          <table className="min-w-full bg-white border border-gray-200 text-sm">
                            <tbody>
                              <tr>
                                <td className="py-2 px-4 font-semibold bg-gray-50">Name</td>
                                <td className="py-2 px-4">{profileData.name || "-"}</td>
                              </tr>
                              <tr>
                                <td className="py-2 px-4 font-semibold bg-gray-50">Email</td>
                                <td className="py-2 px-4">{profileData.pupilEmail || "-"}</td>
                              </tr>
                              <tr>
                                <td className="py-2 px-4 font-semibold bg-gray-50">Mobile</td>
                                <td className="py-2 px-4">{profileData.pupilMobile || "-"}</td>
                              </tr>
                              <tr>
                                <td className="py-2 px-4 font-semibold bg-gray-50">Role</td>
                                <td className="py-2 px-4">{profileData.role || "-"}</td>
                              </tr>
                              <tr>
                                <td className="py-2 px-4 font-semibold bg-gray-50">Status</td>
                                <td className="py-2 px-4">{profileData.status || "-"}</td>
                              </tr>
                              {profileData.currentChitId && (
                                <tr>
                                  <td className="py-2 px-4 font-semibold bg-gray-50">Current Plan ID</td>
                                  <td className="py-2 px-4">{profileData.currentChitId}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )
                    ) : (
                      <div className="text-center text-gray-500 py-4 bg-gray-50 rounded-lg">
                        Click "Load Profile" to view detailed profile information
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      setSelectedMember(null);
                      setProfileData(null);
                    }}
                    className="w-full py-3 px-4 rounded-lg font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MoneyChitDashboard;